import React, { useState, useEffect, useRef } from 'react';
import { Tab, NewsItem, Game, RouteState } from './types';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VideoProvider, useVideo } from './context/VideoContext';
import { ToastProvider } from './context/ToastContext';
import { NavBar } from './components/Layout/NavBar';
import { HomeView } from './components/Views/HomeView';
import { PlayerDetailView } from './components/Views/PlayerDetailView';
import { NewsView } from './components/Views/NewsView';
import { NewsDetailView } from './components/Views/NewsDetailView';
import { WebtoonListView } from './components/Views/WebtoonListView';
import { MagazineListView } from './components/Views/MagazineListView';
import { NewsPostView } from './components/Views/NewsPostView';
import { NewsEditView } from './components/Views/NewsEditView';
import { ShortsView } from './components/Views/ShortsView';
import { GamesView } from './components/Views/GamesView';
import { GameDetailView } from './components/Views/GameDetailView';
import { LoginView } from './components/Views/LoginView';
import { SignUpView } from './components/Views/SignUpView';
import { ChangePasswordView } from './components/Views/ChangePasswordView';
import { MyPageView } from './components/Views/MyPageView';
import { AdminView } from './components/Admin/AdminView';
import { CategoryListView } from './components/Views/CategoryListView';
import { HighlightsView } from './components/Views/HighlightsView';
import { HelpView } from './components/Views/HelpView';
import { PrivacyView } from './components/Views/PrivacyView';
import { ApiInfoView } from './components/Views/ApiInfoView';
import { MatchAnalysisListView } from './components/Views/MatchAnalysisListView';
import { GameStoreView } from './components/GameStore/GameStoreView';
import { CommunityView } from './components/Views/CommunityView';
import PullToRefresh from './components/Common/PullToRefresh';
import SEO from './components/Common/SEO';
import { NeonPitchGameView } from './components/GameStore/NeonPitch/NeonPitchGameView';
import { FootyMasterGameView } from './components/GameStore/FootyMaster';
import { TotoGameView } from './components/GameStore/Toto/TotoGameView';
import { TotoResultView } from './components/GameStore/Toto/TotoResultView';
import { FloatingPlayer } from './components/Common/FloatingPlayer';
import { SidebarSearch } from './components/Common/SidebarSearch';
import { MobileSidebar } from './components/Layout/MobileSidebar';
import { ConfirmModal } from './components/Common/ConfirmModal';
import { NoticeModal } from './components/Common/NoticeModal';
import { GuestTimerBanner } from './components/Common/GuestTimerBanner';
import { t } from './i18n';

// 전역 네비게이션 도우미 및 환경 감지
declare global {
  interface Window {
    navigateTo: (path: string) => void;
    getCurrentPath: () => string;
    isIframe: boolean;
  }
}

// 광고를 표시하면 안 되는 페이지 (게임, 인증, 편집 화면)
const NON_AD_TABS = new Set([
  Tab.GAME_NEON_PITCH,
  Tab.GAME_FOOTY_MASTER,
  Tab.GAME_TOTO,
  Tab.GAME_TOTO_RESULT,
  Tab.GAME_PENALTY_SHOOTOUT,
  Tab.LOGIN,
  Tab.SIGNUP,
  Tab.CHANGE_PASSWORD,
  Tab.NEWS_POST,
  Tab.NEWS_EDIT,
  Tab.ADMIN,
]);

const AppContent = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const { activeVideo } = useVideo();
  const [route, setRoute] = useState<RouteState>({ tab: Tab.HOME });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isStadiumPostModalOpen, setIsStadiumPostModalOpen] = useState(false);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  useEffect(() => {
    const checkModals = () => {
      // Find elements with high z-index that are NOT the NavBar
      const modalElements = document.querySelectorAll('div.fixed, div.absolute');
      const hasModal = Array.from(modalElements).some(el => {
        // Skip NavBar
        if (el.classList.contains('nav-bar-container')) return false;
        
        const zIndex = window.getComputedStyle(el).zIndex;
        const isVisible = window.getComputedStyle(el).display !== 'none' && window.getComputedStyle(el).visibility !== 'hidden';
        return !isNaN(Number(zIndex)) && Number(zIndex) >= 300 && isVisible;
      });
      setIsAnyModalOpen(hasModal);
    };

    const observer = new MutationObserver(checkModals);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    
    return () => observer.disconnect();
  }, []);
  const [gamesViewState, setGamesViewState] = useState<{leagueId: string, date: string} | null>(null);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isIframe = typeof window !== 'undefined' && window.self !== window.top;
  
  const getCurrentPath = () => {
    if (isIframe) {
      return window.location.hash.replace(/^#/, '') || '/';
    }
    return window.location.pathname;
  };

  const navigateTo = (path: string) => {
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    
    if (isIframe) {
      window.location.hash = formattedPath;
    } else {
      window.history.pushState({}, '', formattedPath);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    window.navigateTo = navigateTo;
    window.getCurrentPath = getCurrentPath;
    window.isIframe = isIframe;

    const updateThemeColor = () => {
      const bgColor = route.tab === Tab.ADMIN ? '#f9fafb' : Tab.SHORTS ? '#000000' : '#ffffff';
      const metaTags = document.querySelectorAll('meta[name="theme-color"]');
      metaTags.forEach(tag => {
        tag.setAttribute('content', bgColor);
      });
    };

    updateThemeColor();
  }, [route.tab]);

  useEffect(() => {
    const adsbygoogle = (window as any).adsbygoogle || [];
    adsbygoogle.pauseAdRequests = NON_AD_TABS.has(route.tab) ? 1 : 0;
  }, [route.tab]);

  const handleAuthSuccess = () => {
    const redirectTo = sessionStorage.getItem('redirectAfterLogin');
    if (redirectTo) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigateTo(redirectTo.replace(/^#/, ''));
    } else {
        // 이전 페이지로 이동 시도, 없으면 기본 /
        if (window.history.length > 1) {
            window.history.back();
        } else {
            navigateTo('/');
        }
    }
  };

  const handleSignUpSuccess = () => {
    // 회원가입 성공 시에는 무조건 /games로 이동
    sessionStorage.removeItem('redirectAfterLogin');
    navigateTo('/games');
  };

  const parseUrlToState = (): RouteState => {
    const path = getCurrentPath();
    const segments = path.split('/').filter(Boolean);
    
    if (segments.length === 0) return { tab: Tab.HOME };
    
    const root = segments[0];
    const sub = segments[1];
    const id = segments[2];

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    
    switch (root) {
      case 'player': return { tab: Tab.PLAYER, id: sub };
      case 'feed': 
        if (sub === 'item') return { tab: Tab.NEWS, id: id };
        if (sub === 'webtoon') return { tab: Tab.WEBTOON_LIST };
        if (sub === 'post') return { tab: Tab.NEWS_POST };
        if (sub === 'edit') return { tab: Tab.NEWS_EDIT, id: id };
        if (sub && dateRegex.test(sub)) return { tab: Tab.NEWS, date: sub };
        return { tab: Tab.NEWS };
      case 'games': 
        if (sub) return { tab: Tab.GAMES, id: sub };
        return { tab: Tab.GAMES };
      case 'game': return { tab: Tab.GAME_DETAIL, id: sub };
      case 'team': return { tab: Tab.MYPAGE, subType: 'team_detail', id: sub };
      case 'magazine': return { tab: Tab.MAGAZINE_LIST };
      case 'webtoon': return { tab: Tab.WEBTOON_LIST };
      case 'shorts': return { tab: Tab.SHORTS };
      case 'highlights': 
        if (sub) return { tab: Tab.HIGHLIGHTS, id: sub.replace(/-/g, ' ') };
        return { tab: Tab.HIGHLIGHTS };
      case 'market': 
        if (sub === 'latest_movements') return { tab: Tab.CATEGORY_LIST, subType: 'latest_movements' };
        if (sub === 'major_transfers' || sub === 'top_transfers') return { tab: Tab.CATEGORY_LIST, subType: 'top_transfers' };
        return { tab: Tab.CATEGORY_LIST, subType: sub };
      case 'community': 
        if (sub === 'stadium') {
            if (id) return { tab: Tab.COMMUNITY, subType: 'stadium', id: id };
            return { tab: Tab.COMMUNITY, subType: 'stadium' };
        }
        return { tab: Tab.COMMUNITY };
      case 'gamestore': 
        if (sub?.toLowerCase() === 'neonpitch') return { tab: Tab.GAME_NEON_PITCH };
        if (sub?.toLowerCase() === 'penalty-shootout') return { tab: Tab.GAME_PENALTY_SHOOTOUT };
        if (sub?.toLowerCase() === 'footy-master') return { tab: Tab.GAME_FOOTY_MASTER };
        if (sub?.toLowerCase() === 'toto') {
            if (segments[2]?.toLowerCase() === 'result') return { tab: Tab.GAME_TOTO_RESULT };
            return { tab: Tab.GAME_TOTO };
        }
        return { tab: Tab.GAME_STORE };
      case 'settings':
      case 'mypage': 
      case 'language':
        if (root === 'language') return { tab: Tab.MYPAGE, subType: 'settings' };
        if (sub === 'attendance') return { tab: Tab.MYPAGE, subType: 'attendance' };
        if (sub === 'points') return { tab: Tab.MYPAGE, subType: 'points' };
        if (sub === 'leagues') return { tab: Tab.MYPAGE, subType: 'leagues' };
        if (sub === 'teams') {
          return { tab: Tab.MYPAGE, subType: 'teams' };
        }
        if (sub === 'predictions') return { tab: Tab.MYPAGE, subType: 'match_predictions' };
        if (sub === 'activities') return { tab: Tab.MYPAGE, subType: 'comments' };
        if (sub === 'language') return { tab: Tab.MYPAGE, subType: 'settings' };
        if (sub === 'notices') return { tab: Tab.MYPAGE, subType: 'notices' };
        if (sub === 'sponsorship') return { tab: Tab.MYPAGE, subType: 'sponsorship' };
        return { tab: Tab.MYPAGE };
      case 'login': return { tab: Tab.LOGIN };
      case 'signup': return { tab: Tab.SIGNUP };
      case 'change-password': return { tab: Tab.CHANGE_PASSWORD };
      case 'admin': return { tab: Tab.ADMIN };
      case 'help': return { tab: Tab.HELP };
      case 'about': return { tab: Tab.HELP };
      case 'privacy': return { tab: Tab.PRIVACY, subType: 'privacy' };
      case 'terms': return { tab: Tab.PRIVACY, subType: 'terms' };
      case 'api-info': return { tab: Tab.API_INFO };
      case 'analysis': return { tab: Tab.ANALYSIS };
      default: return { tab: Tab.HOME };
    }
  };

    useEffect(() => {
        const handleSync = () => {
            const newState = parseUrlToState();

            // 로그인한 사용자가 로그인/회원가입/비밀번호찾기 페이지 접근 시 홈으로 리다이렉트
            if (user && [Tab.LOGIN, Tab.SIGNUP, Tab.CHANGE_PASSWORD].includes(newState.tab)) {
                navigateTo('/');
                return;
            }

            setRoute(newState);
        };
    window.addEventListener('popstate', handleSync);
    if (isIframe) window.addEventListener('hashchange', handleSync);
    
    handleSync();
    return () => {
        window.removeEventListener('popstate', handleSync);
        if (isIframe) window.removeEventListener('hashchange', handleSync);
    };
  }, [user]);


  const handleNavigateToPlayer = (id: string) => navigateTo(`/player/${id}`);
  const handleNavigateToNews = (item: NewsItem) => navigateTo(`/feed/item/${item.id}`);
  const handleNavigateToGame = (game: Game) => navigateTo(`/game/${game.id}`);
  const handleNavigateToNewsDate = (date: string | null) => {
      if (date) navigateTo(`/feed/${date}`);
      else navigateTo('/feed');
  };
  
  const handleTabChange = (tId: Tab) => {
    if (tId === Tab.NEWS_POST && !user) {
        sessionStorage.setItem('redirectAfterLogin', '/feed/post');
        setLoginPromptOpen(true);
        return;
    }
    switch (tId) {
      case Tab.HOME: navigateTo('/'); break;
      case Tab.NEWS: navigateTo('/feed'); break;
      case Tab.NEWS_POST: navigateTo('/feed/post'); break;
      case Tab.GAMES: 
        const lastLeagueId = localStorage.getItem('xleven_last_viewed_league');
        navigateTo(`/games/${lastLeagueId || 'all'}`); 
        break;
      case Tab.COMMUNITY: navigateTo('/community'); break;
      case Tab.GAME_STORE: navigateTo('/gamestore'); break;
      case Tab.SHORTS: navigateTo('/shorts'); break;
      case Tab.SETTINGS: navigateTo('/mypage'); break;
      case Tab.ADMIN: navigateTo('/admin'); break;
      default: navigateTo('/');
    }
  };

  const handleBack = () => window.history.length > 1 ? window.history.back() : navigateTo('/');

  useEffect(() => {
    const resetScroll = () => {
      // 1. Reset main window scroll
      window.scrollTo({ top: 0, behavior: 'instant' });
      
      // 2. Reset App-level scroll container
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }

      // 3. Reset any nested scrollable elements (like 'main' or containers inside PullToRefresh)
      const scrollableElements = document.querySelectorAll('main, .overflow-y-auto, .overflow-auto');
      scrollableElements.forEach(el => {
        el.scrollTop = 0;
      });
      
      // Force scroll reset after small delays to ensure it takes effect after layout and data fetching
      const delays = [50, 150, 300];
      delays.forEach(delay => {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'instant' });
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
          const currentScrollables = document.querySelectorAll('main, .overflow-y-auto, .overflow-auto');
          currentScrollables.forEach(el => {
            el.scrollTop = 0;
          });
        }, delay);
      });
    };
    resetScroll();
  }, [route.tab, route.id, route.date]);

  const handleRefresh = async () => {
    // 현재 페이지 데이터 새로고침 로직
    await new Promise(resolve => setTimeout(resolve, 800));
    window.location.reload();
  };

  const showFloatingPlayer = activeVideo && !activeVideo.isPaused && (
    (route.tab !== Tab.GAME_DETAIL || route.id !== activeVideo.matchId) &&
    route.tab !== Tab.HOME
  );

  if (route.tab === Tab.ADMIN) {
      return (
          <div className="min-h-screen bg-gray-50 flex justify-center">
              <div className="w-full max-w-[1400px] flex h-screen relative">
                  <AdminView onExit={() => navigateTo('/')} />
              </div>
          </div>
      );
  }

  return (
    <div className="min-h-screen bg-white font-sans overflow-hidden flex justify-center">
      {route.tab === Tab.HOME && <NoticeModal lang={lang} />}
      {!user && <GuestTimerBanner />}
      
      <MobileSidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)} 
        onNavigate={navigateTo}
      />

      <ConfirmModal 
        isOpen={loginPromptOpen} 
        onClose={() => setLoginPromptOpen(false)} 
        onConfirm={() => { setLoginPromptOpen(false); navigateTo('/login'); }} 
        title={t(lang, 'loginRequiredTitle')} 
        confirmLabel={t(lang, 'signInLabel')} 
      />
      
      <div className="w-full max-w-[1200px] flex h-screen relative">
        <div className="hidden md:block md:w-20 lg:w-64 shrink-0 border-r border-gray-100 z-50 bg-white">
            <NavBar 
              activeTab={
                  route.tab === Tab.ANALYSIS ? null as any :
                  route.tab === Tab.GAME_DETAIL ? Tab.GAMES : 
                  route.tab === Tab.PLAYER ? Tab.HOME : 
                  route.tab === Tab.LOGIN || route.tab === Tab.SIGNUP || route.tab === Tab.MYPAGE || route.tab === Tab.CHANGE_PASSWORD ? Tab.SETTINGS :
                  route.tab
              } 
              onTabChange={handleTabChange} 
              onPostClick={() => setIsStadiumPostModalOpen(true)}
              orientation="vertical"
            />
        </div>

        <main ref={scrollContainerRef} className="flex-1 overflow-hidden h-full bg-white relative">
            <PullToRefresh onRefresh={handleRefresh} disabled={route.tab === Tab.SHORTS || route.tab === Tab.GAME_NEON_PITCH || route.tab === Tab.GAME_FOOTY_MASTER}>
                <div className="min-h-full">
                    {route.tab === Tab.HOME && <HomeView onNavigateToPlayer={handleNavigateToPlayer} onOpenSidebar={() => setIsSidebarOpen(true)} />}
                    {route.tab === Tab.NEWS && !route.id && <NewsView selectedDateFromUrl={route.date} onNavigateToNews={handleNavigateToNews} onDateChange={handleNavigateToNewsDate} onOpenSidebar={() => setIsSidebarOpen(true)} />}
                    {route.tab === Tab.NEWS && route.id && <NewsDetailView key={route.id} newsId={route.id} onBack={handleBack} />}
                    {route.tab === Tab.WEBTOON_LIST && <WebtoonListView onBack={handleBack} lang={lang} />}
                    {route.tab === Tab.MAGAZINE_LIST && <MagazineListView onBack={handleBack} lang={lang} />}
                    {route.tab === Tab.NEWS_POST && <NewsPostView onBack={handleBack} />}
                    {route.tab === Tab.NEWS_EDIT && route.id && <NewsEditView key={route.id} newsId={route.id} onBack={handleBack} />}
                    {route.tab === Tab.PLAYER && <PlayerDetailView key={route.id} playerId={route.id} onBack={handleBack} onNavigateToGame={handleNavigateToGame} />}
                    {route.tab === Tab.GAMES && <GamesView key={route.id || 'all'} selectedLeagueId={route.id} onNavigateToGame={handleNavigateToGame} savedState={gamesViewState} onSaveState={setGamesViewState} onOpenSidebar={() => setIsSidebarOpen(true)} />}
                    {route.tab === Tab.COMMUNITY && (
                      <CommunityView 
                        route={route} 
                        isPostModalOpen={isStadiumPostModalOpen}
                        setIsPostModalOpen={setIsStadiumPostModalOpen}
                      />
                    )}
                    {route.tab === Tab.GAME_STORE && <GameStoreView />}
                    {route.tab === Tab.GAME_NEON_PITCH && <NeonPitchGameView />}
                    {route.tab === Tab.GAME_FOOTY_MASTER && <FootyMasterGameView />}
                    {route.tab === Tab.GAME_TOTO && <TotoGameView />}
                    {route.tab === Tab.GAME_TOTO_RESULT && <TotoResultView onBack={handleBack} />}
                    {route.tab === Tab.GAME_DETAIL && route.id && <GameDetailView key={route.id} gameId={route.id} onBack={handleBack} />}
                    {route.tab === Tab.SHORTS && <ShortsView onOpenSidebar={() => setIsSidebarOpen(true)} />}
                    {route.tab === Tab.HIGHLIGHTS && <HighlightsView lang={lang} />}
                    {route.tab === Tab.CATEGORY_LIST && <CategoryListView type={route.subType as any} onBack={handleBack} onNavigateToPlayer={handleNavigateToPlayer} />}
                    {route.tab === Tab.LOGIN && <LoginView onBack={handleBack} onGoToSignUp={() => navigateTo('/signup')} onLoginSuccess={handleAuthSuccess} />}
                    {route.tab === Tab.SIGNUP && <SignUpView onBack={handleBack} onGoToLogin={() => navigateTo('/login')} onSignUpSuccess={handleSignUpSuccess} />}
                    {route.tab === Tab.CHANGE_PASSWORD && <ChangePasswordView onBack={() => navigateTo('/login')} onSuccess={() => navigateTo('/login')} />}
                    {route.tab === Tab.MYPAGE && <MyPageView initialDetail={route.subType as any} teamId={route.id} onBack={handleBack} onLogout={() => navigateTo('/')} />}
                    {route.tab === Tab.HELP && <HelpView onBack={handleBack} />}
                    {route.tab === Tab.PRIVACY && <PrivacyView initialTab={route.subType as any} onBack={handleBack} />}
                    {route.tab === Tab.API_INFO && <ApiInfoView onBack={handleBack} />}
                    {route.tab === Tab.ANALYSIS && <MatchAnalysisListView />}
                </div>
            </PullToRefresh>
        </main>

        <aside className="hidden md:block w-80 shrink-0 border-l border-gray-100 p-6 sticky top-0 h-full overflow-y-auto bg-white">
            <SidebarSearch onNavigateToPlayer={handleNavigateToPlayer} onNavigateToNews={handleNavigateToNews} />
        </aside>

        <div className="md:hidden nav-bar-container">
            <NavBar 
              activeTab={
                  route.tab === Tab.ANALYSIS ? null as any :
                  route.tab === Tab.GAME_DETAIL ? Tab.GAMES : 
                  route.tab === Tab.PLAYER ? Tab.HOME : 
                  route.tab || Tab.HOME
              } 
              onTabChange={handleTabChange} 
              orientation="horizontal"
              isModalOpen={isAnyModalOpen}
            />
        </div>
      </div>
      {showFloatingPlayer && <FloatingPlayer onReturnToMatch={(mid) => navigateTo(`/game/${mid}`)} />}
    </div>
  );
};

const App = () => (
  <ToastProvider>
    <AuthProvider>
      <VideoProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </VideoProvider>
    </AuthProvider>
  </ToastProvider>
);

export default App;
